import React, { useEffect } from 'react';
import { useLocation, useParams, Link, Navigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../hooks/useLanguage';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { getNewsItemBySlug, getRelatedNews } from '../data/newsData';
export function NewsArticlePage() {
  const {
    t,
    setLocale,
    direction,
    locale
  } = useLanguage();
  const location = useLocation();
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useScrollReveal();
  const {
    ref: relatedRef,
    isVisible: relatedVisible
  } = useScrollReveal();
  const ArrowIcon = direction === 'rtl' ? ArrowRightIcon : ArrowLeftIcon;
  useEffect(() => {
    if (location.pathname.startsWith('/ar')) {
      setLocale('ar');
    } else {
      setLocale('en');
    }
    window.scrollTo(0, 0);
  }, [location.pathname, setLocale]);
  const article = slug ? getNewsItemBySlug(slug) : undefined;
  const relatedNews = slug ? getRelatedNews(slug, 3) : [];
  // Redirect to news page if article not found
  if (!article) {
    return <Navigate to={locale === 'ar' ? '/ar/news' : '/news'} replace data-id="element-884" />;
  }
  const newsPath = locale === 'ar' ? '/ar/news' : '/news';
  return <div className="min-h-screen w-full bg-white text-slate-900" dir={direction} data-id="element-885">
      <Header variant="dark" data-id="element-886" />

      <main data-id="element-887">
        {/* White header background spacer */}
        <div className="bg-white h-20" data-id="element-888" />

        {/* Back to News Bar */}
        <section className="gradient-dark-horizontal py-4 relative overflow-hidden" data-id="element-889">
          <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-890">
            <Link to={newsPath} className="inline-flex items-center gap-2 text-white hover:text-white/80 transition-colors text-sm font-medium" data-id="element-891">
              <ArrowIcon className="w-4 h-4" data-id="element-892" />
              {t('newsArticle.backToNews')}
            </Link>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-12 md:py-16 bg-white relative" ref={contentRef} aria-label="Article Content" data-id="element-893">
          <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-894">
            {/* Article with both side borders */}
            <div className="border-l border-r border-[#CAE3E8] px-6 md:px-12 lg:px-16" data-id="element-895">
              <div className="max-w-4xl mx-auto" data-id="element-896">
                {/* Title */}
                <h1 className={`text-[#061D76] font-bold text-2xl md:text-3xl lg:text-4xl leading-tight mb-8 reveal-hidden ${contentVisible ? 'animate-fade-up' : ''}`} data-id="element-897">
                  {locale === 'ar' ? article.title_ar : article.title}
                </h1>

                {/* Featured Image */}
                <div className={`mb-6 reveal-hidden ${contentVisible ? 'animate-fade-up delay-100' : ''}`} data-id="element-898">
                  <img src={article.image} alt={locale === 'ar' ? article.title_ar : article.title} className="w-full h-auto object-cover" data-id="element-899" />
                </div>

                {/* Date */}
                <p className={`text-[#061D76] font-semibold text-sm mb-8 reveal-hidden ${contentVisible ? 'animate-fade-up delay-200' : ''}`} data-id="element-900">
                  {locale === 'ar' ? article.date_ar : article.date}
                </p>

                {/* Article Body */}
                <div className={`space-y-6 reveal-hidden ${contentVisible ? 'animate-fade-up delay-300' : ''}`} data-id="element-901">
                  {(locale === 'ar' ? article.content_ar : article.content).map((paragraph, index) => <p key={index} className="text-[#061D76]/80 leading-relaxed text-base" data-id="element-902">
                        {paragraph}
                      </p>)}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Read More Section */}
        <section className="py-12 md:py-16 bg-white relative" ref={relatedRef} aria-label="Related Articles" data-id="element-903">
          <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-904">
            <h2 className={`text-[#25B8D6] font-bold text-lg mb-8 reveal-hidden ${relatedVisible ? 'animate-fade-up' : ''}`} data-id="element-905">
              {t('newsArticle.readMore')}
            </h2>

            {/* Related Articles Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-906">
              {relatedNews.map((item, index) => <Link key={item.id} to={locale === 'ar' ? `/ar/news/${item.slug}` : `/news/${item.slug}`} className={`group cursor-pointer transition-shadow duration-300 reveal-hidden ${relatedVisible ? 'animate-fade-up' : ''}`} style={{
              animationDelay: `${(index + 1) * 100}ms`
            }} data-id="element-907">
                  {/* Image */}
                  <div className="aspect-[4/3] overflow-hidden bg-gray-100" data-id="element-908">
                    <img src={item.image} alt={locale === 'ar' ? item.title_ar : item.title} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" data-id="element-909" />
                  </div>

                  {/* Content */}
                  <div className="bg-[#F5F8F8] p-6 flex flex-col min-h-[220px]" data-id="element-910">
                    <span className="text-[#061D76] font-medium text-sm mb-6 block" data-id="element-911">
                      {locale === 'ar' ? item.date_ar : item.date}
                    </span>

                    <h3 className="text-[#061D76] font-bold text-xl leading-snug mb-auto line-clamp-3" data-id="element-912">
                      {locale === 'ar' ? item.title_ar : item.title}
                    </h3>

                    <div className="mt-6 flex justify-end" data-id="element-913">
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={`transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform ${direction === 'rtl' ? 'scale-x-[-1]' : ''}`} data-id="element-914">
                        <path d="M6 16H26M26 16L18 8M26 16L18 24" stroke="#061D76" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-915" />
                      </svg>
                    </div>
                  </div>
                </Link>)}
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-916" />
    </div>;
}